import debounce from 'lodash.debounce'
import { ComparisonResults } from 'components/driving/car-comparison/comparison-results'
import { SubNavComponent } from 'components/driving/sub-nav/sub-nav.component'
import { CarComparisonStore } from 'components/driving/store'
import { ComponentFactory } from 'services/component-factory'
import { isComparisonResults } from 'utils/page'
import { Nullable } from 'constants/types'
import {
    IDrivingStore,
    IDrivingVehicle
} from 'components/driving/driving-interface'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { CLASS_NAME } from 'constants/class-names'
const { ad: adClassName } = CLASS_NAME

/**
 * Indicate radio button in the trim selector modal
 *
 * @returns {boolean}
 */
export const isRadioButton = (): boolean => {
    if (isComparisonResults() && !isMyPicksPage()) {
        return true
    }
    return false
}

/**
 * Indicate my picks page on comparison result page
 *
 * @returns {boolean}
 */
export const isMyPicksPage = (): boolean => {
    if (vehicleIdsInMyPicks() !== vehicleIdsInUrl()) {
        return false
    }
    return true
}

/**
 * Get vehicle ids from my picks result page url
 *
 * @returns {string}
 */
export const vehicleIdsInMyPicks = (): string => {
    const vehicles = CarComparisonStore.getInstance().get()
    const vehicleIds = vehicles.map((vehicle) => vehicle.id)

    if (vehicleIds.length > 0) {
        return vehicleIds.sort().toString()
    } else {
        return ''
    }
}

/**
 * Get vehicle ids from comparison result page url
 *
 * @returns {string}
 */
export const vehicleIdsInUrl = (): string => {
    const currentUrl: URL = new URL(window.location.href)
    let vehicleIds = ''
    if (currentUrl.pathname.includes('comparison-results')) {
        vehicleIds =
            currentUrl.pathname.split('/')[2].split('-').sort().toString() || ''
    }
    return vehicleIds
}

/**
 * Build comparison results full url path based on vehicles data
 *
 * @returns {string}
 */
export const getResultsUrlPath = (
    vehicles: (IDrivingVehicle | undefined)[]
): string => {
    const defaultPath = ComparisonResults.pageUrl
    if (vehicles.length) {
        const idPath = vehicles
            .map((vehicle: IDrivingVehicle) => {
                if (!vehicle) {
                    return undefined
                }
                return vehicle.id
            })
            .filter((id) => id)
            .join('-')
        const namePath = vehicles
            .map((vehicle: IDrivingVehicle) => {
                if (!vehicle) {
                    return undefined
                }
                if (vehicle.slugified_name === '-') {
                    vehicle.slugified_name = 'base-trim'
                }
                return `${vehicle.slugified_full_model_name}-${vehicle.slugified_name}`
            })
            .filter((name) => name)
            .join('-vs-')
        if (idPath && namePath) {
            return `${defaultPath}/${idPath}/${namePath}`
        }
    }
    return `${defaultPath}/`
}

/**
 * Indicate if the vehicle is in my picks session
 *
 * @param {IDrivingStore<IDrivingVehicle>} vehicles
 * @param {string} id
 * @returns {boolean}
 */
export const isVehicleInMyPicks = (
    vehicles: IDrivingStore<IDrivingVehicle>[],
    id: string
): boolean => {
    const onMyPicks = vehicles
        .filter((obj) => obj.id === parseInt(id))
        .map((filtered) => filtered.id)

    if (onMyPicks.length) {
        return true
    }
    return false
}

export class AdCloning extends ComponentBase {
    private adTemplate: HTMLElement | undefined

    public constructor(adTemplateSelector: string) {
        super()

        this.adTemplate = this.makeAdTemplate(adTemplateSelector)
    }

    /**
     * Create a template to be used for adding additional ads into the results
     */
    private makeAdTemplate(adTemplateSelector: string) {
        const adDiv = this.getElement<HTMLElement>(adTemplateSelector)
        if (!adDiv) {
            return
        }

        const adTemplate = <HTMLElement>adDiv.cloneNode(true)
        const adTemplateInner = <HTMLElement>(
            adTemplate.querySelector(`.${adClassName.innerContainer}`)
        )
        adTemplateInner.innerHTML = `<div class="${adClassName.placeholder}">
            <div class="${adClassName.placeholderInner}">
                This advertisement has not loaded yet, but your article continues below.
            </div>
        </div>`
        adTemplateInner.removeAttribute('data-google-query-id')
        adTemplateInner.removeAttribute('data-ad-width')
        return adTemplate
    }

    /**
     * Generate a clone of this.adTemplate with values set properly and return
     * elements needed for inserting and sending event to FEM for filling the
     * slot
     */
    public generateAdDiv():
        | [HTMLElement, HTMLElement]
        | [undefined, undefined] {
        if (!this.adTemplate) {
            return [undefined, undefined]
        }
        const adCounter =
            this.getElements(`.${adClassName.innerContainer}`, document)
                .length + 1
        const adDiv = this.adTemplate.cloneNode(true) as HTMLElement
        const adDivInner = <HTMLElement>(
            adDiv.querySelector(`.${adClassName.inner}`)
        )
        adDivInner.id = `ad__inner-${adCounter}`
        const adDivInnerContainer = <HTMLElement>(
            adDivInner.querySelector(`.${adClassName.innerContainer}`)
        )
        adDivInnerContainer.id = `ad-${adCounter}`
        adDivInnerContainer.setAttribute('data-ad-loc', `${adCounter}`)
        return [adDiv, adDivInnerContainer]
    }

    /**
     * Insert new ad before a reference node
     */
    private insertAd(
        targetElement: HTMLElement,
        referenceNode: ChildNode | null
    ) {
        const [adDiv, adDivInnerContainer] = this.generateAdDiv()
        if (adDiv && targetElement.parentNode) {
            // insert ad before the new targetElement
            targetElement.parentNode.insertBefore(adDiv, referenceNode)

            // call FEM to fill the ad slot via event listener
            dispatchEvent(
                new CustomEvent('adSlotAdded', {
                    detail: { el: [adDivInnerContainer] }
                })
            )
        }
    }

    /**
     * Append new ad before `targetElement`
     */
    public appendAdBefore(targetElement: HTMLElement) {
        return this.insertAd(targetElement, targetElement)
    }

    /**
     * Append new ad after `targetElement`
     */
    public appendAdAfter(targetElement: HTMLElement) {
        return this.insertAd(targetElement, targetElement.nextSibling)
    }
}

/**
 * Load new ads added to HTML content by HTMX injection
 */
export const initiateNewAds = () => {
    const adElements = document.querySelectorAll(
        '[data-ad-not-loaded] .ad__inner__container'
    )
    if (!adElements.length) {
        return
    }

    dispatchEvent(
        new CustomEvent('adSlotAdded', {
            detail: { el: Array.from(adElements) }
        })
    )
    document
        .querySelectorAll('[data-ad-not-loaded]')
        .forEach((el) => el.removeAttribute('data-ad-not-loaded'))
}

export const initSimilarVehiclesAfterSwap = (initiateSubNav: () => void) => {
    /* This function is used to initialize similar vehicles after an HTMX swap.
     * It debounces the initialization to avoid multiple calls in quick succession.
     */
    const wait = 150

    return debounce(() => {
        new ComponentFactory('driving', 'modelCard')
        new ComponentFactory('main', 'carousel')
        initiateSubNav()
        initiateNewAds()
    }, wait)
}
